import type { AxiosError, AxiosResponse } from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiInstance } from "@/shared/api";
import { filterObjectKeys } from "@/shared/lib/utils";
import { useMutate } from "@/shared/hooks";

import { useToastNotifications } from "@/shared/hooks/useToastNotifications";
import type { LeadClaimRequests, UpdateLeadClaimRequest } from "./api.dto";
import type { PaginatedResponse } from "@/shared/types";
import type { LeadClaimRequest } from "../model";

export const useLeadClaimMyRequests = (
  props: LeadClaimRequests & { moderatorId: string },
) => {
  const params = filterObjectKeys(props, ["moderatorId"]);

  return useQuery<PaginatedResponse<LeadClaimRequest>>({
    queryKey: ["leadClaimMyRequests", params],
    queryFn: async () => {
      const { data } = await apiInstance.get<
        PaginatedResponse<LeadClaimRequest>
      >(`/lead-claim-requests/moderator/${props.moderatorId}`, params);
      return data;
    },
  });
};

export const useLeadClaimRequests = (props: LeadClaimRequests) => {
  const params = filterObjectKeys(props);

  return useQuery<PaginatedResponse<LeadClaimRequest>>({
    queryKey: ["leadClaimMyRequests", params],
    queryFn: async () => {
      const { data } = await apiInstance.get<
        PaginatedResponse<LeadClaimRequest>
      >(`/lead-claim-requests`, params);
      return data;
    },
  });
};

export const useCreateLeadClaimRequest = () => {
  const { onSuccess, onError } = useMutate({
    method: "post",
    url: "/lead-claim-requests",
    toast: {
      success: {
        description: "Заявка на привязку лида создана.",
      },
    },
  });

  return useMutation<AxiosResponse, AxiosError, FormData>({
    mutationKey: ["createLeadClaimRequest"],
    mutationFn: (data) =>
      apiInstance.post("/lead-claim-requests", data, {
        headers: { "Content-Type": "multipart/form-data" },
      }),
    onSuccess,
    onError,
  });
};

export const useUpdateLeadClaimRequest = () => {
  const queryClient = useQueryClient();

  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: {
        description: "Статус лида обновлен успешно.",
      },
    },
    refetchQueries: ["leadStatus"],
  });

  return useMutation<AxiosResponse, AxiosError, UpdateLeadClaimRequest>({
    mutationFn: ({ id, ...data }) => {
      return apiInstance.patch(`/lead-claim-requests/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leadClaimMyRequests"] });
      onSuccess();
    },
    onError,
  });
};
